<template>
    <footer class="bg-base-100 py-6 lg:py-12 -mx-4">
        <div class="container m-auto">
            <div class="grid grid-cols-8 gap-10">
                <div class="col-span-8 lg:col-span-2">
                    <div class="grid grid-cols-2 gap-8">
                        <div class="col-span-2 sm:col-span-1 lg:col-span-2">
                            <LayoutLogo
                                :media="media"
                                width="292px"
                                width-mobile="292px"
                                height="86px"
                                height-mobile="86px"
                            />
                        </div>

                        <div class="col-span-2 sm:col-span-1 lg:col-span-2">
                            <div class="mb-8">
                                <div class="text-xl mb-1 text-headline font-semibold">
                                    {{ t('layout.footer.consulting.title') }}
                                </div>
                                <p class="text-lg leading-6">
                                    {{ t('layout.footer.consulting.text') }}
                                </p>
                            </div>

                            <div>
                                <p class="mb-2 text-lg">
                                    {{ t('layout.footer.consulting.germany') }}<br>{{ t('layout.footer.consulting.freeTel') }} <a :href="`tel:${t('layout.footer.consulting.germanyTel')}`">{{ t('layout.footer.consulting.germanyTel') }}</a>
                                </p>
                                <p class="mb-2 text-lg">
                                    {{ t('layout.footer.consulting.austria') }}<br>{{ t('layout.footer.consulting.freeTel') }} <a :href="`tel:${t('layout.footer.consulting.austriaTel')}`">{{ t('layout.footer.consulting.austriaTel') }}</a>
                                </p>
                                <p class="mb-2 text-lg">
                                    {{ t('layout.footer.consulting.international') }}<br> <a :href="`tel:${t('layout.footer.consulting.internationalTel')}`">{{ t('layout.footer.consulting.internationalTel') }}</a><br> {{ t('layout.footer.consulting.charged') }}
                                </p>
                                <p class="mb-2 text-lg">
                                    {{ t('layout.footer.consulting.opening') }}
                                </p>
                                <p class="text-lg">
                                    {{ t('layout.footer.consulting.openingText') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-span-8 lg:col-span-6 flex flex-col gap-10">
                    <div class="flex flex-col sm:flex-row gap-8 flex-wrap">
                        <div class="w-full flex-1">
                            <div class="sm:max-w-[192px] text-center sm:text-left">
                                <MiscLink
                                    :to="t('layout.footer.services.shippingUrl')"
                                    class="inline-flex sm:block hover:underline"
                                >
                                    <div class="aspect-square relative mb-4 hidden sm:block">
                                        <img v-lazy-load 
                                            data-src="/assets/images/shipping.png"
                                            :alt="t('layout.footer.services.shipping')"
                                            class="max-w-[192px] rounded w-full h-full object-cover"
                                            loading="lazy">
                                    </div>
                                    <div class="flex gap-1 items-center">
                                        <span class="w-full leading-5 font-semibold text-lg">{{ t('layout.footer.services.shipping') }}</span>
                                        <MiscIcon name="chevron-right" class="w-auto" size="lg" />
                                    </div>
                                </MiscLink>
                            </div>
                        </div>
                        <div class="w-full flex-1">
                            <div class="sm:max-w-[192px] text-center sm:text-left">
                                <MiscLink
                                    :to="t('layout.footer.services.deliveryServiceUrl')"
                                    class="inline-flex sm:block hover:underline"
                                >
                                    <div class="aspect-square relative mb-4 hidden sm:block">
                                        <img v-lazy-load 
                                            data-src="/assets/images/delivery-service.png"
                                            :alt="t('layout.footer.services.deliveryService')"
                                            class="max-w-[192px] rounded w-full h-full object-cover"
                                            loading="lazy">
                                    </div>

                                    <div class="flex gap-1 items-center">
                                        <span class="w-full leading-5 font-semibold text-lg">{{ t('layout.footer.services.deliveryService') }}</span>
                                        <MiscIcon name="chevron-right" class="w-auto" size="lg" />
                                    </div>
                                </MiscLink>
                            </div>
                        </div>
                        <div class="w-full flex-1">
                            <div class="sm:max-w-[192px] text-center sm:text-left">
                                <MiscLink
                                    :to="t('layout.footer.services.locationsUrl')"
                                    class="inline-flex sm:block hover:underline"
                                >
                                    <div class="aspect-square relative mb-4 hidden sm:block">
                                        <img v-lazy-load 
                                            data-src="/assets/images/locations.png"
                                            :alt="t('layout.footer.services.locations')"
                                            class="max-w-[192px] rounded w-full h-full object-cover"
                                            loading="lazy">
                                    </div>

                                    <div class="flex gap-1 items-center">
                                        <span class="w-full leading-5 font-semibold text-lg">{{ t('layout.footer.services.locations') }}</span>
                                        <MiscIcon name="chevron-right" class="w-auto" size="lg" />
                                    </div>
                                </MiscLink>
                            </div>
                        </div>
                        <div class="w-full flex-auto max-w-[141px]">
                            <div id="trustbadgeCustomContainer" />
                            <div id="trustbadgeCustomMobileContainer" />
                        </div>
                    </div>

                    <div class="flex gap-8 flex-col sm:flex-row flex-wrap">
                        <div class="w-full flex-1">
                            <div class="font-semibold text-xl mb-1">
                                {{ t('layout.footer.categoryService.title') }}
                            </div>
                            <ul class="mb-6">
                                <li v-for="(item, index) in data?.categoryService?.value" :key="index">
                                    <MiscLink
                                        :to="item.link"
                                        class="text-primary-content text-lg hover:underline"
                                        :class="{
                                            'font-semibold': item.highlighted,
                                        }"
                                    >
                                        {{ item.title }}
                                    </MiscLink>
                                </li>
                            </ul>

                            <div v-if="data?.facebook?.value || data?.instagram?.value">
                                <p class="text-lg">
                                    {{ t('layout.footer.categoryService.socialMedia') }}
                                </p>
                                <div class="flex gap-2">
                                    <MiscLink
                                        v-if="data?.facebook?.value && data?.facebookLink?.value"
                                        :to="data?.facebookLink?.value"
                                        target="_blank"
                                        class="flex gap-2 items-center"
                                    >
                                        <svg
                                            width="32"
                                            height="32"
                                            viewBox="0 0 32 32"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M27.545 29.0373C28.369 29.0373 29.0371 28.3692 29.0371 27.545V3.49224C29.0371 2.66796 28.3691 2 27.545 2H3.49224C2.66796 2 2 2.66796 2 3.49224V27.545C2 28.3691 2.66786 29.0373 3.49224 29.0373H27.545Z" fill="#3C5A99" />
                                            <path d="M21.2123 30.5038V18.8977H25.1079L25.6912 14.3746H21.2123V11.4869C21.2123 10.1773 21.576 9.28489 23.4538 9.28489L25.8489 9.28384V5.23833C25.4347 5.18321 24.0129 5.06006 22.3588 5.06006C18.9056 5.06006 16.5415 7.16794 16.5415 11.0389V14.3746H12.6359V18.8977H16.5415V30.5038H21.2123Z" fill="white" />
                                        </svg>
                                    </MiscLink>
                                    <MiscLink
                                        v-if="data?.instagram?.value && data?.instagramLink?.value"
                                        :to="data?.instagramLink?.value"
                                        target="_blank"
                                        class="flex gap-2 items-center"
                                    >
                                        <img v-lazy-load 
                                            :data-src="instagramImg"
                                            alt="Instagram"
                                            width="32"
                                            height="32"
                                            class=""
                                            loading="lazy"
                                        >
                                    </MiscLink>
                                </div>
                            </div>
                        </div>
                        <div class="w-full flex-1">
                            <div class="font-semibold text-xl mb-1">
                                {{ t('layout.footer.categoryBuy.title') }}
                            </div>
                            <ul class="mb-6">
                                <li v-for="(item, index) in data?.categoryBuy?.value" :key="index">
                                    <MiscLink
                                        :to="item.link"
                                        class="text-primary-content text-lg hover:underline"
                                    >
                                        {{ item.title }}
                                    </MiscLink>
                                </li>
                            </ul>

                            <div>
                                <p class="text-lg">
                                    {{ t('layout.footer.categoryBuy.text') }}
                                </p>

                                <div class="flex gap-1">
                                    <img v-lazy-load 
                                        v-for="icon in activeShippingIcons"
                                        :key="icon.name"
                                        :data-src="icon.src"
                                        :alt="icon.alt"
                                        width="43"
                                        height="31"
                                        class="w-[43px]"
                                        loading="lazy"
                                    >
                                </div>
                            </div>
                        </div>
                        <div class="w-full flex-1">
                            <div class="font-semibold text-xl mb-1">
                                {{ t('layout.footer.payment.title') }}
                            </div>

                            <div class="mb-6">
                                <MiscLink
                                    :to="t('layout.footer.payment.link')"
                                    class="text-primary-content text-lg hover:underline"
                                >
                                    {{ t('layout.footer.payment.methods') }}
                                </MiscLink>
                                <p class="text-lg">
                                    {{ t('layout.footer.payment.methodsText') }}
                                </p>
                            </div>

                            <div>
                                <p class="text-lg">
                                    {{ t('layout.footer.payment.text') }}
                                </p>

                                <div class="flex gap-1">
                                    <img v-lazy-load 
                                        v-for="icon in activePaymentIcons"
                                        :key="icon.name"
                                        :data-src="icon.src"
                                        :alt="icon.alt"
                                        width="43"
                                        height="31"
                                        class="w-[43px]"
                                        loading="lazy"
                                    >
                                </div>
                            </div>
                        </div>
                        <div class="w-full flex-auto max-w-[141px] hidden lg:block" />
                    </div>

                    <div v-if="locale === 'de'">
                        <div class="font-semibold text-xl mb-2">
                            {{ t('layout.footer.app.title') }}
                        </div>
                        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
                            <a
                                :href="t('layout.footer.app.googlePlayUrl')"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="inline-block"
                            >
                                <img v-lazy-load 
                                    :data-src="googlePlayBadge"
                                    :alt="t('layout.footer.app.googlePlayAlt')"
                                    class="h-10 w-auto"
                                    loading="lazy"
                                >
                            </a>
                            <a
                                :href="t('layout.footer.app.iosUrl')"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="text-primary-content text-lg underline"
                            >
                                {{ t('layout.footer.app.ios') }}
                            </a>
                        </div>
                    </div>
                </div>

                <div class="col-span-8">
                    <p class="text-headline font-semibold mb-4 text-lg">
                        {{ t('layout.footer.copyright', { 'year': new Date().getFullYear() }) }}
                    </p>

                    <div class="mb-4 flex gap-1 flex-wrap flex-col sm:gap-6 sm:flex-row">
                        <MiscLink
                            v-for="(item, index) in data?.bottomNavigation?.value"
                            :key="index"
                            :to="item.link"
                            class="text-primary-content text-lg hover:underline"
                            :class="{
                                'font-semibold': item.highlighted,
                            }"
                        >
                            {{ item.title }}
                        </MiscLink>
                    </div>

                    <LayoutFooterLinks />
                </div>
            </div>
        </div>
    </footer>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import rechnungImg from '~/assets/images/payment/rechnung.svg'
import paypalImg from '~/assets/images/payment/paypal.svg'
import sepaImg from '~/assets/images/payment/sepa.svg'
import visaImg from '~/assets/images/payment/visa.svg'
import masterImg from '~/assets/images/payment/master.svg'
import dhlImg from '~/assets/images/shipping/dhl.svg'
import dpdImg from '~/assets/images/shipping/dpd.svg'
import googlePlayBadge from '~/assets/images/google-play-badge-de.svg'
import instagramImg from '~/assets/images/socials/instagram.png'

const { t, locale } = useI18n()

const props = defineProps<{
    data: any,
    media: any
}>()

const activePaymentIcons = computed(() => {
    const iconConfig = [
        { name: 'invoice', src: rechnungImg, alt: 'Rechnung' },
        { name: 'paypal', src: paypalImg, alt: 'PayPal' },
        { name: 'sepa', src: sepaImg, alt: 'SEPA' },
        { name: 'visa', src: visaImg, alt: 'Visa' },
        { name: 'mastercard', src: masterImg, alt: 'Master Card' }
    ]
    return iconConfig.filter(icon => props.data?.[icon.name]?.value)
})

const activeShippingIcons = computed(() => {
    const iconConfig = [
        { name: 'dhl', src: dhlImg, alt: 'DHL' },
        { name: 'dpd', src: dpdImg, alt: 'DPD' }
    ]
    return iconConfig.filter(icon => props.data?.[icon.name]?.value)
})
</script>
